import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {gql} from "@apollo/client";
import cn from "classnames";

import WithTooltip from "../../ui/tooltip";
import {Avatar} from "../../schema";

import classes from "./AvatarSelect.module.css";

type props = React.ComponentProps<"div"> & {
  name?: string
  value?: string
  onChange?: (e: React.FormEvent) => void
  avatars: Avatar[]
};

export default function AvatarSelect({avatars, name, value, onChange, className: extraClassName, ...props}: props) {
  const {t} = useTranslation();

  const labels = useMemo(
    () => ({
      Max: t("components.AvatarSelect.Max"),
      Hana: t("components.AvatarSelect.Hana"),
      Julia: t("components.AvatarSelect.Julia"),
      Ken: t("components.AvatarSelect.Ken"),
      Kate: t("components.AvatarSelect.Kate"),
      John: t("components.AvatarSelect.John"),
      Evan: t("components.AvatarSelect.Evan"),
      Chloe: t("components.AvatarSelect.Chloe"),
      "No character": t("components.AvatarSelect.No character"),
    }),
    [t],
  );

  return (
    <div className={cn(classes.root, extraClassName)} {...props}>
      <div className={classes.choices}>
        {avatars.map(avatar => (
          <WithTooltip
            key={avatar.id}
            as="label"
            className={classes.choice}
            helpText={labels[avatar.name as keyof typeof labels] ?? avatar.name}
          >
            <input
              className={classes.input}
              type="radio"
              name={name}
              checked={avatar.id === value}
              value={avatar.id}
              onChange={onChange}
            />

            <div className={classes.inner}>
              <img
                src={avatar.preview}
                alt={avatar.name}
              />
            </div>
          </WithTooltip>
        ))}
      </div>
    </div>
  )
}

AvatarSelect.fragments = {
  root: gql`
    fragment AvatarSelect on Avatar {
      id
      name(languageCode: $language)
      preview
    }
  `
}
